import Button from '../../components/common/Button';
import { getCompaniesCondition } from '../../types/companies';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBuilding } from '@fortawesome/free-solid-svg-icons';
import styles from './styles.module.css';
import { useEffect, useState } from 'react';
import CheckBoxGroup, {
  CheckBoxGroupOnChangeProps,
  ValueObj,
} from '../../components/common/CheckBoxGroup';
import Input from '../../components/common/Input';
import Select from '../../components/common/Select';
import Pagenation from '../../components/common/Pagenation';
import CompaniesTable from './components/CompaniesTable';
import {
  useCreateCompanyMutation,
  useGetCompaniesQuery,
  useGetCompanyTypesQuery,
} from '../../api/companyApi';
import { useGetWasteItemsQuery } from '../../api/otherApi';
import EditSideBar, {
  CompanyFormDataProvider,
  CompanyFormDataProviderProps,
} from './components/EditSideBar';
import { generateCompanyFormData } from './generateCompanyFormData';
import PolicyRequired from '../../components/common/PolicyRequired';
import CsvDownload from '../../components/CsvDownload';
import CsvInput from '../../components/CsvInput';
import HeaderLayout from '../../components/HeaderLayout';
import SelectPageSize from '../../components/common/SelectPageSize';
import { logger } from '../../services/logger';
import NoResultFound from '../../components/common/NoResultFound';
import { keyList, useKeyAction } from '../../services/useKeyAction';

const initConditions: Omit<getCompaniesCondition, 'offset' | 'page' | 'limit'> =
  {
    companyName: '',
    companyTypeId: null,
    companyWasteItemIds: [],
  };
function Companies() {
  const [conditions, updateConditions] = useState<getCompaniesCondition>({
    ...initConditions,
    page: 1,
    limit: 20,
  });
  const { data: companyTypes = [] } = useGetCompanyTypesQuery();
  const companyTypesOptions = companyTypes.map((c) => ({
    label: c.companyType,
    value: `${c.companyTypeId}`,
  }));
  const { data: wasteItems } = useGetWasteItemsQuery();
  const [wasteItemCheckBoxGroupValues, updateWasteItemCheckBoxGroupValues] =
    useState<ValueObj[]>([]);
  useEffect(() => {
    updateWasteItemCheckBoxGroupValues(
      (wasteItems || []).map((item, i) => ({
        value: `${item.wasteItemId}`,
        label: `${i + 1}.${item.wasteItem}`,
        checked: false,
      }))
    );
  }, [wasteItems]);
  const onPage = (page: number) => {
    updateConditions((prev) => ({
      ...prev,
      page,
    }));
  };
  const onChangePerPage = (perPage: string) => {
    updateConditions((prev) => ({ ...prev, page: 1, limit: Number(perPage) }));
  };
  const [companyName, setCompanyName] = useState(initConditions.companyName);
  const onChangeCompanyName = (name: string) => {
    setCompanyName(name);
  };
  const [companyType, setCompanyType] = useState<number | null>(null);
  const onChangeCompanyType = (type: string) => {
    if (type === '') {
      setCompanyType(null);
    }

    setCompanyType(Number(type));
  };
  const [wasteItemIds, setWasteItemIds] = useState<number[]>([]);
  const onChangeWasteItem = ({
    values,
    selectedValues,
  }: CheckBoxGroupOnChangeProps) => {
    updateWasteItemCheckBoxGroupValues(values);
    setWasteItemIds(selectedValues.map((v) => Number(v)));
  };
  const onClear = () => {
    setCompanyName('');
    setCompanyType(null);
    setWasteItemIds([]);
    updateWasteItemCheckBoxGroupValues(
      wasteItemCheckBoxGroupValues.map((v) => ({ ...v, checked: false }))
    );
    updateConditions((prev) => ({
      ...prev,
      companyName: '',
      companyTypeId: null,
      companyWasteItemIds: [],
      page: 1,
    }));
  };
  const onSubmit = () => {
    updateConditions((prev) => ({
      ...prev,
      companyName,
      companyTypeId: companyType,
      companyWasteItemIds: wasteItemIds,
      page: 1,
    }));
  };
  const { data: { total, companies } = { total: 0, companies: [] } } =
    useGetCompaniesQuery(conditions);

  const [formData, setFormData] = useState<CompanyFormDataProviderProps | null>(
    null
  );
  const [createCompany] = useCreateCompanyMutation();
  const onCreateCompany = () => {
    setFormData({
      formData: generateCompanyFormData(),
      type: 'create',
      onSubmit: (data) => {
        // 会社区分がよって必要のないデータを除去する
        if (data.companyTypeId === /* 顧客 */ 2) {
          delete data.companyWasteItemIds;
        } else if (data.companyTypeId === /* 処分事業者 */ 3) {
          delete data.businessTypeId;
        }

        createCompany(data)
          .unwrap()
          .then(() => {
            setFormData(null);
          })
          .catch(logger.error);
      },
    });
  };

  const onConditionEnter = useKeyAction(keyList.ENTER, () => {
    onSubmit();
  });

  return (
    <HeaderLayout
      title="会社情報"
      controls={
        <div className={styles.buttons}>
          <PolicyRequired policy={27}>
            <div className={styles.button}>
              <CsvDownload type={'company'} />
            </div>
            <div className={styles.button}>
              <CsvInput type={'company'} />
            </div>
          </PolicyRequired>
          <PolicyRequired policy={24}>
            <div className={styles.button}>
              <Button
                variants={['secondary', 'contain']}
                onClick={onCreateCompany}
              >
                <FontAwesomeIcon icon={faBuilding} className={styles.icon} />
                会社登録
              </Button>
            </div>
          </PolicyRequired>
        </div>
      }
    >
      <PolicyRequired policy={28}>
        <div className={styles.conditions} onKeyDown={onConditionEnter}>
          <div className={styles.conditionInputs}>
            <div className={styles.companyName}>
              <Input
                value={companyName}
                onChange={(e) => onChangeCompanyName(e.target.value)}
                variants={['fit']}
                placeholder="会社名"
              ></Input>
            </div>
            <div className={styles.companyTypes}>
              <Select
                options={companyTypesOptions}
                value={companyType !== null ? `${companyType}` : ''}
                onChange={(e) => onChangeCompanyType(e.target.value)}
                variants={['fit']}
                placeholder="会社区分"
              ></Select>
            </div>
            <div className={styles.wasteItems}>
              <CheckBoxGroup
                values={wasteItemCheckBoxGroupValues}
                onChange={onChangeWasteItem}
                placeholder="処理区分"
              ></CheckBoxGroup>
            </div>
          </div>
          <div className={styles.conditionButtons}>
            <div className={styles.clearButton}>
              <Button variants={['contain', 'cancel']} onClick={onClear}>
                クリア
              </Button>
            </div>
            <Button
              variants={['contain', 'secondary', 'search']}
              onClick={onSubmit}
            >
              検索
            </Button>
          </div>
        </div>
      </PolicyRequired>
      {companies.length > 0 ? (
        <>
          <div className={styles.pageContainer}>
            <SelectPageSize
              value={`${conditions.limit}`}
              onChange={(e) => onChangePerPage(e.target.value)}
            />
            <div>
              <Pagenation
                page={conditions.page || 1}
                onPage={onPage}
                perPage={conditions.limit || 0}
                total={total}
              ></Pagenation>
            </div>
          </div>
          <div className={styles.content}>
            <CompaniesTable
              companies={companies}
              openEdit={setFormData}
              sidebarClose={() => {
                setFormData(null);
              }}
            />
          </div>
        </>
      ) : (
        <div className={styles.noResultFound}>
          <NoResultFound />
        </div>
      )}
      <CompanyFormDataProvider value={formData}>
        <EditSideBar />
      </CompanyFormDataProvider>
    </HeaderLayout>
  );
}

export default Companies;
